import { ReactNode, useState } from 'react'
import { Button, Tooltip } from '@systemeio/components'
import { File02Icon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { TemplatesFilterInterface } from '../types/templates-filter-interface'
import { TemplatesInterface } from '../types/templates-interface'

interface TemplatesListItemProps {
  template: TemplatesInterface
  onChangeCategory: (category: number | null) => void
  selectedCategory: TemplatesFilterInterface['category']
}

const TemplatesListItem = ({
  template,
  onChangeCategory,
  selectedCategory,
}: TemplatesListItemProps): ReactNode => {
  const { t } = useTranslation()

  const [isButtonFocused, setIsButtonFocused] = useState(false)

  const handleButtonFocus = (): void => {
    setIsButtonFocused(true)
  }

  const handleButtonBlur = (): void => {
    setIsButtonFocused(false)
  }

  return (
    <div className="group flex flex-col overflow-x-hidden rounded-lg shadow-2">
      <div className="relative aspect-video overflow-hidden">
        <img
          className="block size-full object-cover object-center"
          alt={template.preview?.filename}
          src={template.preview?.path}
        />
        {template.hasMultipleSteps && (
          <Tooltip
            trigger={<File02Icon aria-hidden="true" className="size-5" />}
            triggerProps={{
              'aria-label': t('public.templates.funnel_template'),
              className:
                'absolute! top-3 left-3 z-10 bg-text-primary hover:bg-text-primary [&_svg]:size-6',
              render: <Button variant="gray" size="icon-md" />,
            }}
            popupProps={{ className: 'w-max' }}
            content={t('public.templates.funnel_template')}
          />
        )}
        <div
          className={cn(
            `absolute inset-0 flex size-full items-center justify-center gap-3 bg-text-primary/0 transition-all duration-200 ease-linear group-hover:bg-text-primary/50 group-hover:backdrop-blur-xs`,
            isButtonFocused && 'bg-text-primary/50 backdrop-blur-xs',
          )}
        >
          {template.previewUrl && (
            <Button
              variant="primary"
              onFocus={handleButtonFocus}
              onBlur={handleButtonBlur}
              onMouseLeave={handleButtonBlur}
              className={cn(
                'transition-opacity ease-linear',
                isButtonFocused && 'opacity-100',
                `opacity-0 duration-200 group-hover:opacity-100`,
              )}
              render={<LinkWithoutPrefetch href={template.previewUrl} target={'_blank'} />}
            >
              {t('public.templates.preview')}
            </Button>
          )}
          <Button
            variant="primary"
            onFocus={handleButtonFocus}
            onBlur={handleButtonBlur}
            onMouseLeave={handleButtonBlur}
            className={cn(
              'transition-opacity duration-200 ease-linear',
              isButtonFocused && 'opacity-100',
              `opacity-0 group-hover:opacity-100`,
            )}
            render={<LinkWithoutPrefetch href={`/funnel/share/${template.hash}`} locale={false} />}
          >
            {t('public.templates.select')}
          </Button>
        </div>
      </div>
      <div className={'flex flex-col gap-3 px-3 py-6'}>
        {template.categories.length > 0 && (
          <div className={'flex flex-wrap gap-1.5'}>
            {template.categories.map(category => (
              <button
                disabled={selectedCategory === category.id}
                onClick={() => onChangeCategory(category.id)}
                key={category.id}
                className={cn(
                  'outline-hidden main-transition-colors',
                  selectedCategory !== category.id && 'hover:text-text-link',
                  `rounded-md bg-bg-secondary/30 px-2 py-1.5 text-[13px] text-text-secondary focus-visible:focus-ring-primary`,
                )}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplatesListItem
